import { useCallback, useEffect, useMemo, useRef } from 'react'
import { toast } from 'react-toastify'

import { Card, type WCCard } from '@loadsmart/miranda-react'

import { useAnalytics } from 'core/analytics/useAnalytics'

import {
  P44_PROVIDER_LINK,
  TrackingMethod,
  TrackingProvider,
  TT_PROVIDER_LINK,
} from '../../../domain/Tracking'
import {
  formatFinalizeConnectionButtonText,
  formatFinalizeConnectionStepSubtitle,
  formatFinalizeConnectionStepTitle,
} from '../../../presenters/preferredTrackingMethod'
import APIFinalizeConnectionContent from './APIFinalizeConnectionContent'
import EDIFinalizeConnectionContent from './EDIFinalizeConnectionContent'
import ELDFinalizeConnectionContent from './ELDFinalizeConnectionContent'
import ManualFinalizeConnectionContent from './ManualFinalizeConnectionContent'
import MobileFinalizeConnectionContent from './MobileFinalizeConnectionContent'
import { useTrackingOnboardingContext } from './TrackingOnboardingContext'

export type FinalizeConnectionStepProps = {
  readonly selectedMethod?: TrackingMethod
  readonly selectedProvider?: TrackingProvider
  readonly onConfirmed: () => void
}

function openProviderWebsite(
  hasPreviousTracking: boolean,
  method?: TrackingMethod,
  provider?: TrackingProvider
) {
  if (hasPreviousTracking) return
  if (method !== TrackingMethod.ELD) return

  if (provider === TrackingProvider.P44) {
    window.open(P44_PROVIDER_LINK, '_blank', 'noopener')
  }

  if (provider === TrackingProvider.TT) {
    window.open(TT_PROVIDER_LINK, '_blank', 'noopener')
  }
}

function showContent(selectedMethod?: TrackingMethod, selectedProvider?: TrackingProvider) {
  if (selectedMethod === TrackingMethod.EDI || selectedMethod === TrackingMethod.Manual) {
    return true
  }

  return Boolean(selectedProvider && selectedMethod)
}

export default function FinalizeConnectionStep({
  selectedMethod,
  selectedProvider,
  onConfirmed,
}: FinalizeConnectionStepProps) {
  const { track } = useAnalytics()
  const cardRef = useRef<WCCard>(null)

  const {
    isPendingUpdate,
    isDoneUpdate,
    hasErrorUpdate,
    hasPreviousTracking,
    updatePreferredTrackingMethod,
  } = useTrackingOnboardingContext()

  const handleOnConfirm = useCallback(() => {
    updatePreferredTrackingMethod({ method: selectedMethod, provider: selectedProvider })
    track('Saved: Tracking setup drawer', {
      method: selectedMethod,
      provider: selectedProvider,
    })
  }, [selectedMethod, selectedProvider, track, updatePreferredTrackingMethod])

  const cardContent = useMemo(() => {
    switch (selectedMethod) {
      case TrackingMethod.API:
        return <APIFinalizeConnectionContent />
      case TrackingMethod.EDI:
        return <EDIFinalizeConnectionContent />
      case TrackingMethod.Manual:
        return <ManualFinalizeConnectionContent />
      case TrackingMethod.Mobile:
        return <MobileFinalizeConnectionContent selectedProvider={selectedProvider} />
      case TrackingMethod.ELD:
        return <ELDFinalizeConnectionContent selectedProvider={selectedProvider} />
    }
  }, [selectedMethod, selectedProvider])

  const showCardContent = showContent(selectedMethod, selectedProvider)

  useEffect(() => {
    if (isDoneUpdate) {
      toast.success('Preferred tracking connection confirmed.')
      onConfirmed()
      openProviderWebsite(hasPreviousTracking, selectedMethod, selectedProvider)
    }
  })

  return (
    <Card collapsible={showCardContent} ref={cardRef}>
      <Card.Title>{formatFinalizeConnectionStepTitle(hasPreviousTracking)}</Card.Title>
      {showCardContent && (
        <>
          <Card.Subtitle>{formatFinalizeConnectionStepSubtitle(hasPreviousTracking)}</Card.Subtitle>
          <Card.Divider />
          <Card.Body>{cardContent}</Card.Body>
          <Card.Footer>
            <Card.ActionPrimary
              onClick={handleOnConfirm}
              loading={isPendingUpdate}
              variant={hasErrorUpdate ? 'danger' : 'primary'}
            >
              {formatFinalizeConnectionButtonText({
                method: selectedMethod,
                provider: selectedProvider,
                hasErrorUpdate,
                hasPreviousTracking,
              })}
            </Card.ActionPrimary>
          </Card.Footer>
        </>
      )}
    </Card>
  )
}
